import React from 'react';
import { Card, Col, Row, Table } from 'react-bootstrap';
import { Job } from 'services/api/job';
import {
  DULL_CHAR_CODES,
  GAUGE_CODES,
  INNER_OUTER_SCALE,
  LOCATION_CODES,
  REASON_PULLED_CODES,
  StepProps,
  calcAV,
  calcAvgROP,
  calcBHACumulative,
  calcEquivalentHoleSize,
  calcGPM,
  calcTotalInterval
} from '../types/types';
import { Form } from 'react-bootstrap';

interface Step6Props extends StepProps {
  job: Job;
}

const KV: React.FC<{ label: string; value: any; wide?: boolean }> = ({
  label,
  value,
  wide
}) => (
  <div className={`pjr-kv-item ${wide ? 'col-12 col-md-6' : ''}`}>
    <div className="kv-label">{label}</div>
    <div className="kv-value">
      {value !== '' && value !== null && value !== undefined ? (
        String(value)
      ) : (
        <span className="text-muted fst-italic">—</span>
      )}
    </div>
  </div>
);

const SectionHeading: React.FC<{ title: string }> = ({ title }) => (
  <h6 className="fw-bold fs-9 text-uppercase text-primary mt-4 mb-2 pb-1 border-bottom border-primary border-opacity-25">
    {title}
  </h6>
);

const Step6ReviewExport: React.FC<Step6Props> = ({
  reportData,
  onChange,
  job
}) => {
  const rd = reportData;

  const totalInterval = calcTotalInterval(rd.urDepthIn, rd.urDepthOut);
  const avgROP = calcAvgROP(totalInterval, rd.totalDrillHours);
  const gpm = calcGPM(
    rd.annularVelocity.pumpOutput,
    rd.annularVelocity.totalSPM
  );
  const eqHoleSize = calcEquivalentHoleSize(rd.openingDia, rd.pilotHoleDia);
  const avDPOpenHole = calcAV(
    gpm,
    rd.annularVelocity.idOfHole,
    rd.annularVelocity.odOfDP
  );
  const avDCOpenHole = calcAV(
    gpm,
    rd.annularVelocity.idOfHole,
    rd.annularVelocity.odOfCollars
  );
  const avDPCasing = calcAV(
    gpm,
    rd.annularVelocity.idOfCasing,
    rd.annularVelocity.odOfDP
  );
  const avDCCasing = calcAV(
    gpm,
    rd.annularVelocity.idOfCasing,
    rd.annularVelocity.odOfCollars
  );
  const cumulatives = calcBHACumulative(rd.bhaComponents);
  const totalBHALength = cumulatives[cumulatives.length - 1] ?? 0;

  return (
    <div>
      {/* ── Job Header Summary ────────────────────────────────────────────────── */}
      <Card className="pjr-section-card mb-3">
        <Card.Body className="py-2 px-3">
          <Row className="g-2 fs-9">
            <Col md={3}>
              <span className="fw-semibold text-muted">Customer: </span>
              <span className="fw-bold">
                {job.customer?.organizationName || '—'}
              </span>
            </Col>
            <Col md={3}>
              <span className="fw-semibold text-muted">Contractor: </span>
              <span className="fw-bold">{rd.contractor || '—'}</span>
            </Col>
            <Col md={3}>
              <span className="fw-semibold text-muted">Rig: </span>
              <span className="fw-bold">{job.rig?.name || '—'}</span>
            </Col>
            <Col md={3}>
              <span className="fw-semibold text-muted">Job #: </span>
              <span className="fw-bold">{job.jobNumber || '—'}</span>
            </Col>
            <Col md={3}>
              <span className="fw-semibold text-muted">Well: </span>
              <span className="fw-bold">{job.well || '—'}</span>
            </Col>
            <Col md={3}>
              <span className="fw-semibold text-muted">Tool Type: </span>
              <span className="fw-bold">{rd.toolType || '—'}</span>
            </Col>
            <Col md={3}>
              <span className="fw-semibold text-muted">Supervisor: </span>
              <span className="fw-bold">
                {job.supervisor?.[0]
                  ? `${job.supervisor[0].firstName} ${job.supervisor[0].lastName}`
                  : '—'}
              </span>
            </Col>
            <Col md={3}>
              <span className="fw-semibold text-muted">Company Rep: </span>
              <span className="fw-bold">
                {job.customerRepresentative
                  ? `${job.customerRepresentative.firstName} ${job.customerRepresentative.lastName}`
                  : '—'}
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── Step 1 Summary ───────────────────────────────────────────────────── */}
      <SectionHeading title="Well & Tool Setup" />
      <div className="pjr-kv-grid mb-2">
        <KV label="Rig Location" value={rd.rigLocation} />
        <KV label="Rig Type" value={rd.rigType} />
        <KV label="Rotary Drive" value={rd.rotaryDrive} />
        <KV label="Lease" value={rd.lease} />
        <KV label="Block" value={rd.block} />
        <KV label="Well #" value={rd.wellNumber} />
        <KV label="Parish" value={rd.parish} />
        <KV label="Well Type" value={rd.wellType} />
        <KV
          label="Water Depth"
          value={rd.waterDepth !== '' ? `${rd.waterDepth} ft` : ''}
        />
        <KV label="Directional" value={rd.wellDirectional ? 'YES' : 'NO'} />
        <KV label="Tight Hole" value={rd.tightHole ? 'YES' : 'NO'} />
        <KV
          label="Max DLS"
          value={rd.maxDLS !== '' ? `${rd.maxDLS} °/100ft` : ''}
        />
        <KV label="Tool Series" value={rd.toolSeries} />
        <KV
          label="Pilot Hole Dia"
          value={rd.pilotHoleDia !== '' ? `${rd.pilotHoleDia}"` : ''}
        />
        <KV
          label="Opening Dia"
          value={rd.openingDia !== '' ? `${rd.openingDia}"` : ''}
        />
        <KV label="Tool OD" value={rd.toolOD !== '' ? `${rd.toolOD}"` : ''} />
      </div>

      {/* ── Step 2 Summary ───────────────────────────────────────────────────── */}
      <SectionHeading title="Drilling Parameters" />
      <div className="pjr-kv-grid mb-2">
        <KV
          label="UR Depth In"
          value={rd.urDepthIn !== '' ? `${rd.urDepthIn} ft` : ''}
        />
        <KV
          label="UR Depth Out"
          value={rd.urDepthOut !== '' ? `${rd.urDepthOut} ft` : ''}
        />
        <KV
          label="Total Interval"
          value={totalInterval !== null ? `${totalInterval} ft` : ''}
        />
        <KV
          label="Total Drill Hours"
          value={rd.totalDrillHours !== '' ? `${rd.totalDrillHours} hrs` : ''}
        />
        <KV
          label="Avg ROP"
          value={avgROP !== null ? `${avgROP.toFixed(1)} ft/hr` : ''}
        />
        <KV label="Mud Type" value={rd.mudType} />
        <KV
          label="Casing Size"
          value={rd.casingSize !== '' ? `${rd.casingSize}"` : ''}
        />
        <KV
          label="Casing Wt"
          value={rd.casingWeight !== '' ? `${rd.casingWeight} lbs/ft` : ''}
        />
        <KV
          label="Casing ID"
          value={rd.casingID !== '' ? `${rd.casingID}"` : ''}
        />
        <KV
          label="Set At"
          value={rd.casingSetAt !== '' ? `${rd.casingSetAt} ft` : ''}
        />
      </div>

      {rd.runs.length > 0 && (
        <>
          <p className="fs-10 text-muted mb-1">
            {rd.runs.length} Run(s) recorded
          </p>
          <div style={{ overflowX: 'auto' }}>
            <Table size="sm" bordered className="fs-10">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Serial #</th>
                  <th>Depth In</th>
                  <th>Depth Out</th>
                  <th>Interval</th>
                  <th>Hrs DC</th>
                  <th>Hrs Drill</th>
                  <th>ROP</th>
                  <th>WOB Lo-Hi</th>
                  <th>RPM Lo-Hi</th>
                  <th>Flow (gpm)</th>
                  <th>Mud Wt</th>
                </tr>
              </thead>
              <tbody>
                {rd.runs.map((run, i) => {
                  const ri = calcTotalInterval(run.urDepthIn, run.urDepthOut);
                  const rr = calcAvgROP(ri, run.totHoursDrilling);
                  return (
                    <tr key={i}>
                      <td>{run.runNumber}</td>
                      <td>{run.serialNumber || '—'}</td>
                      <td>
                        {run.urDepthIn !== '' ? `${run.urDepthIn}'` : '—'}
                      </td>
                      <td>
                        {run.urDepthOut !== '' ? `${run.urDepthOut}'` : '—'}
                      </td>
                      <td>{ri !== null ? `${ri}'` : '—'}</td>
                      <td>{run.totHoursDC !== '' ? run.totHoursDC : '—'}</td>
                      <td>
                        {run.totHoursDrilling !== ''
                          ? run.totHoursDrilling
                          : '—'}
                      </td>
                      <td>{rr !== null ? `${rr.toFixed(1)}` : '—'}</td>
                      <td>
                        {run.wobLo !== '' && run.wobHi !== ''
                          ? `${run.wobLo}–${run.wobHi}k`
                          : '—'}
                      </td>
                      <td>
                        {run.rpmLo !== '' && run.rpmHi !== ''
                          ? `${run.rpmLo}–${run.rpmHi}`
                          : '—'}
                      </td>
                      <td>{run.flowRate !== '' ? run.flowRate : '—'}</td>
                      <td>
                        {run.mudWeight !== '' ? `${run.mudWeight} ppg` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </>
      )}

      {/* ── Step 4 BHA Summary ───────────────────────────────────────────────── */}
      <SectionHeading
        title={`BHA (${
          rd.bhaComponents.length
        } components — Total: ${totalBHALength.toFixed(2)} ft)`}
      />
      <div style={{ overflowX: 'auto' }}>
        <Table size="sm" bordered className="fs-10 mb-2">
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Component</th>
              <th>Mfg</th>
              <th>Serial #</th>
              <th>OD</th>
              <th>ID</th>
              <th>Length (ft)</th>
              <th>Cumul (ft)</th>
            </tr>
          </thead>
          <tbody>
            {rd.bhaComponents.map((c, i) => (
              <tr key={i}>
                <td>{c.itemNumber}</td>
                <td>{c.component || '—'}</td>
                <td>{c.mfg || '—'}</td>
                <td>{c.serialNumber || '—'}</td>
                <td>{c.od !== '' ? c.od : '—'}</td>
                <td>{c.id !== '' ? c.id : '—'}</td>
                <td className="fw-semibold">
                  {c.length !== '' ? c.length : '—'}
                </td>
                <td className="fw-bold text-primary">
                  {cumulatives[i].toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* ── Step 5 Engineering Summary ───────────────────────────────────────── */}
      <SectionHeading title="Engineering Calculations" />
      <Row className="g-2 mb-3">
        {[
          [
            'Equiv. Hole Size',
            eqHoleSize !== null ? `${eqHoleSize.toFixed(3)}"` : '—'
          ],
          ['Calc. GPM', gpm !== null ? `${gpm.toFixed(0)} gpm` : '—'],
          [
            'AV DP × Open Hole',
            avDPOpenHole !== null ? `${avDPOpenHole.toFixed(1)} ft/min` : '—'
          ],
          [
            'AV DC × Open Hole',
            avDCOpenHole !== null ? `${avDCOpenHole.toFixed(1)} ft/min` : '—'
          ],
          [
            'AV DP × Casing',
            avDPCasing !== null ? `${avDPCasing.toFixed(1)} ft/min` : '—'
          ],
          [
            'AV DC × Casing',
            avDCCasing !== null ? `${avDCCasing.toFixed(1)} ft/min` : '—'
          ]
        ].map(([label, value]) => (
          <Col md={2} key={label}>
            <div className="border rounded p-2 text-center">
              <div className="fs-10 text-muted">{label}</div>
              <div className="fw-bold text-primary">{value}</div>
            </div>
          </Col>
        ))}
      </Row>

      {/* ── Daily Log Preview ────────────────────────────────────────────────── */}
      {rd.jobLog.length > 0 && (
        <>
          <SectionHeading title={`Daily Log (${rd.jobLog.length} entries)`} />
          {rd.jobLog.map((entry, i) => (
            <div
              key={i}
              className="mb-2 ps-3 border-start border-primary border-2"
            >
              <div className="fw-bold fs-9">{entry.date}</div>
              <div className="fs-9 text-800">
                {entry.narrative || (
                  <span className="text-muted fst-italic">No narrative</span>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {/* ── Comments ─────────────────────────────────────────────────────────── */}
      <SectionHeading title="Comments" />
      <Form.Control
        as="textarea"
        rows={4}
        placeholder="Enter any additional comments or observations..."
        value={rd.comments}
        onChange={e => onChange({ comments: e.target.value })}
        className="mb-3"
        style={{ resize: 'vertical' }}
      />

      {/* ── Bit Grading ──────────────────────────────────────────────────────── */}
      <SectionHeading title="Bit Grading (IADC Standard)" />
      <Card className="pjr-section-card purple mb-3">
        <Card.Body className="py-3">
          <Row className="g-3">
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Inner Row (0–8)
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={rd.bitGrading.innerRow}
                  onChange={e =>
                    onChange({
                      bitGrading: { ...rd.bitGrading, innerRow: e.target.value }
                    })
                  }
                >
                  {INNER_OUTER_SCALE.map(v => (
                    <option key={v}>{v}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Outer Row (0–8)
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={rd.bitGrading.outerRow}
                  onChange={e =>
                    onChange({
                      bitGrading: { ...rd.bitGrading, outerRow: e.target.value }
                    })
                  }
                >
                  {INNER_OUTER_SCALE.map(v => (
                    <option key={v}>{v}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Dull Char
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={rd.bitGrading.dullChar}
                  onChange={e =>
                    onChange({
                      bitGrading: { ...rd.bitGrading, dullChar: e.target.value }
                    })
                  }
                >
                  {DULL_CHAR_CODES.map(v => (
                    <option key={v}>{v}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Location
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={rd.bitGrading.location}
                  onChange={e =>
                    onChange({
                      bitGrading: { ...rd.bitGrading, location: e.target.value }
                    })
                  }
                >
                  {LOCATION_CODES.map(v => (
                    <option key={v}>{v}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">Gauge</Form.Label>
                <Form.Select
                  size="sm"
                  value={rd.bitGrading.gauge}
                  onChange={e =>
                    onChange({
                      bitGrading: { ...rd.bitGrading, gauge: e.target.value }
                    })
                  }
                >
                  {GAUGE_CODES.map(v => (
                    <option key={v}>{v}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Other Dull Char
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={rd.bitGrading.otherDullChar}
                  onChange={e =>
                    onChange({
                      bitGrading: {
                        ...rd.bitGrading,
                        otherDullChar: e.target.value
                      }
                    })
                  }
                >
                  {DULL_CHAR_CODES.map(v => (
                    <option key={v}>{v}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Reason Pulled
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={rd.bitGrading.reasonPulled}
                  onChange={e =>
                    onChange({
                      bitGrading: {
                        ...rd.bitGrading,
                        reasonPulled: e.target.value
                      }
                    })
                  }
                >
                  {REASON_PULLED_CODES.map(v => (
                    <option key={v}>{v}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Bearing
                </Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="E / F / N or 0–8"
                  value={rd.bitGrading.bearing}
                  onChange={e =>
                    onChange({
                      bitGrading: { ...rd.bitGrading, bearing: e.target.value }
                    })
                  }
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── IADC Bit Grading Reference ────────────────────────────────────────── */}
      <details className="mb-3">
        <summary className="fs-9 fw-semibold text-muted cursor-pointer mb-2">
          Show IADC Bit Grading Reference
        </summary>
        <Table size="sm" bordered className="pjr-bit-ref-table fs-10 mt-2">
          <thead>
            <tr>
              <th>Col</th>
              <th>Name</th>
              <th>Values / Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>1</td>
              <td>Inner Rows</td>
              <td>0–8 (0=no wear, 8=total tooth loss)</td>
            </tr>
            <tr>
              <td>2</td>
              <td>Outer Rows</td>
              <td>0–8</td>
            </tr>
            <tr>
              <td>3</td>
              <td>Dull Char</td>
              <td>{DULL_CHAR_CODES.join(', ')}</td>
            </tr>
            <tr>
              <td>4</td>
              <td>Location</td>
              <td>N=Nose, M=Middle, G=Gauge, A=All Rows, 1/2/3=Cone #</td>
            </tr>
            <tr>
              <td>5</td>
              <td>Bearing/Seals</td>
              <td>
                Sealed: E=Effective, F=Failed, N=Not gradable. Non-sealed: 0–8
              </td>
            </tr>
            <tr>
              <td>6</td>
              <td>Gauge</td>
              <td>IN=In gauge, 1=1/16" out, 2=1/8" out, 3=1/4" out, 4+=etc</td>
            </tr>
            <tr>
              <td>7</td>
              <td>Other Dull Char</td>
              <td>Same codes as column 3</td>
            </tr>
            <tr>
              <td>8</td>
              <td>Reason Pulled</td>
              <td>{REASON_PULLED_CODES.join(', ')}</td>
            </tr>
          </tbody>
        </Table>
      </details>
    </div>
  );
};

export default Step6ReviewExport;
